import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  adminGetStats, adminGetUsers, adminGetUserDetail, adminBanUser, adminToggleAdmin, adminDeleteUser, adminWatchUser,
  adminGetPosts, adminDeletePost, adminGetReports, adminHandleReport, adminGenerateRecipe,
  adminGetBanners, adminCreateBanner, adminUpdateBanner, adminDeleteBanner,
  adminGetAdConfig, adminUpdateAdConfig, adminGetAdStats
} from '../lib/api';
import {
  Users, FileText, AlertTriangle, ChefHat, BarChart3,
  Search, Ban, Shield, ShieldAlert, ShieldOff, Trash2, Eye, Check, X, RefreshCw,
  Loader2, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Menu, LogOut, Megaphone, Plus, Edit2, ToggleLeft, ToggleRight, Settings, UserX, Crown, Gift, Clock, Bell, Send, MessageSquare, AlertCircle, CheckCircle, Palette, Image, Upload, Scale, Footprints, Dumbbell, UtensilsCrossed, MapPin, Mail, Server, Lock, TestTube, ScrollText, Save, Key, Languages, Globe, HardDrive, Sparkles, Phone, Trophy, UserMinus, Ticket, CreditCard, Zap, Bug, Headphones, Home, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

// ═══════════════════════════════════════════════════════════════
// Admin tabs are code-split with React.lazy so the initial /admin
// bundle stays small. The admin typically interacts with one tab
// at a time — loading all 12 upfront wasted ~50-70KB gzipped.
// Webpack automatically emits a separate chunk per dynamic import.
// ═══════════════════════════════════════════════════════════════
const RemindersTab      = lazy(() => import('../components/admin/RemindersTab'));
const CommunicationsTab = lazy(() => import('../components/admin/CommunicationsTab'));
const DashboardButtonsTab = lazy(() => import('../components/admin/DashboardButtonsTab'));
const LegalTab          = lazy(() => import('../components/admin/LegalTab'));
const ModerationTab     = lazy(() => import('../components/admin/ModerationTab'));
const CredentialsTab    = lazy(() => import('../components/admin/CredentialsTab'));
const SettingsTab       = lazy(() => import('../components/admin/SettingsTab'));
const UsersTab          = lazy(() => import('../components/admin/UsersTab'));
const PostsTab          = lazy(() => import('../components/admin/PostsTab'));
const ReportsTab        = lazy(() => import('../components/admin/ReportsTab'));
const ReportedMessagesTab = lazy(() => import('../components/admin/ReportedMessagesTab'));
const PaymentsTab         = lazy(() => import('../components/admin/PaymentsTab'));
const ChallengesTab     = lazy(() => import('../components/admin/ChallengesTab'));
const AdsTab            = lazy(() => import('../components/admin/AdsTab'));
const ChefTab           = lazy(() => import('../components/admin/ChefTab'));
const PromoCodesTab     = lazy(() => import('../components/admin/PromoCodesTab'));
const LifetimePromoTab  = lazy(() => import('../components/admin/LifetimePromoTab'));
const TransactionsTab   = lazy(() => import('../components/admin/TransactionsTab'));
const StatsTab          = lazy(() => import('../components/admin/StatsTab'));
const QueueTab          = lazy(() => import('../components/admin/QueueTab'));
const BannerModal       = lazy(() => import('../components/admin/BannerModal'));
const ServiceBalances   = lazy(() => import('../components/admin/ServiceBalances'));
const BugsTab           = lazy(() => import('../components/admin/BugsTab'));
const SupportTab        = lazy(() => import('../components/admin/SupportTab'));
const TeamTab           = lazy(() => import('../components/admin/TeamTab'));
const RolePermissionsTab = lazy(() => import('../components/admin/RolePermissionsTab'));
const MoveTab            = lazy(() => import('../components/admin/MoveTab'));

// Sidebar visibility is now driven by the backend `accessible_sections`
// list (returned from /auth/me). When the backend can't tell us — e.g.
// during a brief auth refresh — we fall back to "show everything" so
// the master is never locked out by a transient empty state.
function canAccessSection(accessibleSections, sectionId) {
  if (!Array.isArray(accessibleSections)) return true;
  return accessibleSections.includes(sectionId);
}

/** Skeleton shown while a lazy admin tab chunk is loading. */
function AdminTabSkeleton() {
  return (
    <div className="space-y-3 animate-pulse" data-testid="admin-tab-skeleton">
      <div className="h-10 bg-gray-800 rounded-lg" />
      <div className="h-28 bg-gray-800 rounded-xl" />
      <div className="h-28 bg-gray-800 rounded-xl" />
      <div className="h-28 bg-gray-800 rounded-xl" />
    </div>
  );
}

const API = API_URL;

// Custom Confirmation Modal Component
function ConfirmModal({ isOpen, title, message, confirmText, cancelText, onConfirm, onCancel, type = 'danger' }) {
  if (!isOpen) return null;
  
  const colors = {
    danger: { bg: 'bg-red-500', icon: AlertTriangle, iconColor: 'text-red-500' },
    warning: { bg: 'bg-yellow-500', icon: AlertCircle, iconColor: 'text-yellow-500' },
    success: { bg: 'bg-green-500', icon: CheckCircle, iconColor: 'text-green-500' },
  };
  
  const config = colors[type] || colors.danger;
  const IconComponent = config.icon;
  
  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-[60] p-4"
      onClick={onCancel}
    >
      <div 
        className="w-full max-w-sm bg-gray-800 rounded-2xl p-5 text-center"
        onClick={e => e.stopPropagation()}
      >
        <div className={`w-14 h-14 rounded-full ${config.bg}/20 flex items-center justify-center mx-auto mb-4`}>
          <IconComponent className={`w-7 h-7 ${config.iconColor}`} />
        </div>
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-400 text-sm mb-5 whitespace-pre-line">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl bg-gray-700 text-gray-300 font-medium text-sm"
          >
            {cancelText || 'Annulla'}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 py-2.5 rounded-xl ${config.bg} text-white font-medium text-sm`}
          >
            {confirmText || 'Conferma'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Toast/Alert Component
function Toast({ isOpen, message, type = 'error', onClose }) {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  const colors = {
    error: 'bg-red-500/20 border-red-500/50 text-red-200',
    success: 'bg-green-500/20 border-green-500/50 text-green-200',
    warning: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-200',
  };
  
  return (
    <div className="fixed bottom-20 left-4 right-4 z-[70] flex justify-center">
      <div className={`px-4 py-3 rounded-xl border ${colors[type]} text-sm max-w-sm text-center shadow-lg`}>
        {message}
      </div>
    </div>
  );
}

// Helper to safely extract error message from API responses
function getErrorMessage(err, fallback = 'Errore') {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || 'Errore').join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || fallback;
  }
  return fallback;
}

// Stat Card Component
function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-xs text-gray-400 truncate">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Mobile-friendly User Card
function ResetPasswordDialog({ userName, onCancel, onConfirm }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (password.length < 6) return;
    if (password !== confirm) return;
    setSubmitting(true);
    try {
      await onConfirm(password);
    } finally {
      setSubmitting(false);
    }
  };

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 6;

  return (
    <div 
      className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-0 sm:p-4"
      onClick={onCancel}
    >
      <div 
        className="w-full sm:max-w-md bg-gray-800 rounded-t-2xl sm:rounded-2xl p-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-purple-400" />
            <h3 className="text-lg font-bold text-white">Reset Password</h3>
          </div>
          <button onClick={onCancel} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-gray-400 mb-4 text-sm">
          Imposta una nuova password per <strong className="text-white">{userName}</strong>.
          Tutte le sessioni attive verranno terminate.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Nuova password (min. 6 caratteri)</label>
            <input
              data-testid="admin-reset-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2.5 bg-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {tooShort && <p className="text-red-400 text-xs mt-1">La password deve avere almeno 6 caratteri</p>}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Conferma password</label>
            <input
              data-testid="admin-reset-password-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2.5 bg-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {mismatch && <p className="text-red-400 text-xs mt-1">Le password non coincidono</p>}
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-lg bg-gray-700 text-gray-300 text-sm font-medium"
          >
            Annulla
          </button>
          <button
            data-testid="admin-reset-password-confirm-btn"
            onClick={handleSubmit}
            disabled={submitting || password.length < 6 || password !== confirm}
            className="flex-1 py-2.5 rounded-lg bg-purple-500 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Salvataggio...' : 'Conferma'}
          </button>
        </div>
      </div>
    </div>
  );
}


// Mobile-friendly Post Card

// Mobile-friendly Banner Card



export default function AdminPanel() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop sidebar collapse state — persisted in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('admin_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('admin_sidebar_collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Users state
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editContactModal, setEditContactModal] = useState(null); // { user_id, name, email, phone }
  const [resetPasswordModal, setResetPasswordModal] = useState(null); // { user_id, name }

  // Challenges admin state
  const [challenges, setChallenges] = useState([]);
  const [challengesTotal, setChallengesTotal] = useState(0);
  const [challengeSearch, setChallengeSearch] = useState('');
  const [challengeStatus, setChallengeStatus] = useState('all');
  const [challengePage, setChallengePage] = useState(1);
  const [selectedChallenge, setSelectedChallenge] = useState(null); // { challenge, posts }
  const [deleteChallengeModal, setDeleteChallengeModal] = useState(null);
  const [deleteChallengeConfirmText, setDeleteChallengeConfirmText] = useState('');
  const [removeParticipantModal, setRemoveParticipantModal] = useState(null);

  // Posts state
  const [posts, setPosts] = useState([]);
  const [postsPage, setPostsPage] = useState(1);
  const [postsTotal, setPostsTotal] = useState(0);
  const [previewPost, setPreviewPost] = useState(null);

  // Reports state
  const [reports, setReports] = useState([]);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsTotal, setReportsTotal] = useState(0);

  // Chef state
  const [generating, setGenerating] = useState(false);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);
  const [selectedMealType, setSelectedMealType] = useState('pranzo');

  // Ads state
  const [banners, setBanners] = useState([]);
  const [adStats, setAdStats] = useState(null);
  const [adConfig, setAdConfig] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [bannerFilter, setBannerFilter] = useState('');
  const [deletingBanner, setDeletingBanner] = useState(null);

  // Settings state
  const [appSettings, setAppSettings] = useState({ google_login_enabled: false, phone_login_enabled: true });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Premium management state
  const [giftModal, setGiftModal] = useState(null);
  const [giftMonths, setGiftMonths] = useState(1);
  const [giftLifetime, setGiftLifetime] = useState(false);
  const [giftReason, setGiftReason] = useState('');

  // Dashboard Buttons state
  const [dashboardButtons, setDashboardButtons] = useState([
    { id: 'weight', name: 'Inserisci Peso', icon: 'Scale', gradientFrom: '#16A34A', gradientTo: '#14B8A6', backgroundImage: '', opacity: 1 },
    { id: 'chef', name: 'AI', icon: 'Sparkles', gradientFrom: '#8B5CF6', gradientTo: '#EC4899', backgroundImage: '', opacity: 1 },
    { id: 'workout', name: 'Allenamenti', icon: 'Dumbbell', gradientFrom: '#8B5CF6', gradientTo: '#A855F7', backgroundImage: '', opacity: 1 },
    { id: 'walk', name: 'Camminata GPS', icon: 'Footprints', gradientFrom: '#0EA5E9', gradientTo: '#06B6D4', backgroundImage: '', opacity: 1 },
  ]);
  const [editingButton, setEditingButton] = useState(null);
  const [savingButtons, setSavingButtons] = useState(false);
  const [uploadingButtonImage, setUploadingButtonImage] = useState(false);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'danger' });
  
  // Toast state
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'error' });
  
  // API Credentials state
  // (Credentials state + handlers moved to <CredentialsTab />.)
  
  const showToast = (message, type = 'error') => {
    setToast({ isOpen: true, message, type });
  };
  
  const showConfirm = (title, message, onConfirm, type = 'danger') => {
    setConfirmModal({ isOpen: true, title, message, onConfirm, type });
  };
  
  const closeConfirm = () => {
    setConfirmModal({ ...confirmModal, isOpen: false });
  };
  const loadStats = useCallback(async () => {
    try {
      const res = await adminGetStats();
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Accesso negato. Devi essere admin.');
      } else {
        setError('Errore nel caricamento');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await adminGetUsers(usersPage, userSearch);
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, [usersPage, userSearch]);

  const loadPosts = useCallback(async () => {
    try {
      const res = await adminGetPosts(postsPage);
      setPosts(res.data.posts);
      setPostsTotal(res.data.total);
    } catch (err) {
      console.error('Error loading posts:', err);
    }
  }, [postsPage]);

  const loadReports = useCallback(async () => {
    try {
      const res = await adminGetReports('pending', reportsPage);
      setReports(res.data.reports);
      setReportsTotal(res.data.total);
    } catch (err) {
      console.error('Error loading reports:', err);
    }
  }, [reportsPage]);

  const loadChallenges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/challenges`, {
        params: { page: challengePage, limit: 20, search: challengeSearch, status: challengeStatus },
        withCredentials: true
      });
      setChallenges(res.data.challenges || []);
      setChallengesTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error loading challenges:', err);
    }
  }, [challengePage, challengeSearch, challengeStatus]);

  const openChallengeDetail = async (challengeId) => {
    try {
      const res = await axios.get(`${API}/api/admin/challenges/${challengeId}`, { withCredentials: true });
      setSelectedChallenge(res.data);
    } catch (err) {
      console.error('Error loading challenge detail:', err);
      showToast('Errore nel caricamento dettaglio', 'error');
    }
  };

  const confirmDeleteChallenge = async () => {
    if (!deleteChallengeModal) return;
    try {
      await axios.delete(`${API}/api/admin/challenges/${deleteChallengeModal.challenge_id}`, { withCredentials: true });
      setDeleteChallengeModal(null);
      setDeleteChallengeConfirmText('');
      setSelectedChallenge(null);
      loadChallenges();
      showToast('Challenge eliminata', 'success');
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Errore eliminazione', 'error');
    }
  };

  const confirmRemoveParticipant = async () => {
    if (!removeParticipantModal) return;
    try {
      await axios.delete(
        `${API}/api/admin/challenges/${removeParticipantModal.challenge_id}/participants/${removeParticipantModal.user_id}`,
        { withCredentials: true }
      );
      setRemoveParticipantModal(null);
      if (selectedChallenge?.challenge?.challenge_id === removeParticipantModal.challenge_id) {
        openChallengeDetail(removeParticipantModal.challenge_id);
      }
      loadChallenges();
      showToast('Partecipante rimosso', 'success');
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Errore rimozione', 'error');
    }
  };

  const loadAds = useCallback(async () => {
    try {
      const [bannersRes, configRes, statsRes] = await Promise.all([
        adminGetBanners(bannerFilter),
        adminGetAdConfig(),
        adminGetAdStats()
      ]);
      setBanners(bannersRes.data);
      setAdConfig(configRes.data);
      setAdStats(statsRes.data);
    } catch (err) {
      console.error('Error loading ads:', err);
    }
  }, [bannerFilter]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/settings`, { withCredentials: true });
      setAppSettings(res.data);
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  }, []);

  const saveSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/api/settings`, newSettings, { withCredentials: true });
      // Merge new settings with existing ones instead of replacing
      setAppSettings(prev => ({ ...prev, ...newSettings }));
      return true;
    } catch (err) {
      console.error('Error saving settings:', err);
      showToast('Errore nel salvare le impostazioni', 'error');
      return false;
    } finally {
      setSavingSettings(false);
    }
  };

  // Legal content — extracted into <LegalTab /> component (state + handlers
  // live there). Keep loadLegal as a no-op alias for the activeTab useEffect.
  const loadLegal = useCallback(() => {}, []);

  // API Credentials functions
  // Credentials state + handlers — extracted to <CredentialsTab />.
  const loadCredentials = useCallback(() => {}, []);

  const updateUserContact = async () => {
    if (!editContactModal) return;
    try {
      const payload = {};
      payload.email = editContactModal.email || null;
      payload.phone = editContactModal.phone || null;
      await axios.patch(`${API}/api/admin/users/${editContactModal.user_id}/contact`, payload, { withCredentials: true });
      setEditContactModal(null);
      loadUsers();
      if (selectedUser?.user?.user_id === editContactModal.user_id) {
        const res = await adminGetUserDetail(editContactModal.user_id);
        setSelectedUser(res.data);
      }
      showToast('Contatti aggiornati', 'success');
    } catch (err) {
      console.error('Error updating contact:', err);
      const msg = err?.response?.data?.detail || 'Errore nell\'aggiornamento dei contatti';
      showToast(msg, 'error');
    }
  };

  const resetUserPassword = async (newPassword) => {
    if (!resetPasswordModal) return;
    try {
      await axios.post(`${API}/api/admin/users/${resetPasswordModal.user_id}/reset-password`, 
        { password: newPassword }, 
        { withCredentials: true }
      );
      setResetPasswordModal(null);
      showToast('Password resettata con successo', 'success');
    } catch (err) {
      console.error('Error resetting password:', err);
      const msg = err?.response?.data?.detail || 'Errore nel reset della password';
      showToast(msg, 'error');
    }
  };

  const giftPremium = async () => {
    if (!giftModal) return;
    try {
      await axios.post(`${API}/api/premium/admin/gift`, {
        user_id: giftModal.user_id,
        months: giftLifetime ? 0 : giftMonths,
        reason: giftReason
      }, { withCredentials: true });
      setGiftModal(null);
      setGiftMonths(1);
      setGiftLifetime(false);
      setGiftReason('');
      loadUsers();
      showToast('Premium regalato con successo!', 'success');
    } catch (err) {
      console.error('Error gifting premium:', err);
      const msg = err?.response?.data?.detail || 'Errore nel regalare Premium';
      showToast(msg, 'error');
    }
  };

  const revokePremium = async (userId) => {
    showConfirm(
      'Revocare Premium',
      'Sei sicuro di voler revocare il Premium a questo utente?',
      async () => {
        closeConfirm();
        try {
          await axios.post(`${API}/api/premium/admin/revoke`, { user_id: userId }, { withCredentials: true });
          loadUsers();
          if (selectedUser?.user?.user_id === userId) {
            const res = await adminGetUserDetail(userId);
            setSelectedUser(res.data);
          }
          showToast('Premium revocato', 'success');
        } catch (err) {
          console.error('Error revoking premium:', err);
          showToast('Errore nella revoca', 'error');
        }
      },
      'warning'
    );
  };

  const endTrial = async (userId) => {
    showConfirm(
      'Terminare Trial',
      'Sei sicuro di voler terminare il trial di questo utente?\nNon avrà più accesso Premium.',
      async () => {
        closeConfirm();
        try {
          await axios.post(`${API}/api/premium/admin/end-trial/${userId}`, {}, { withCredentials: true });
          loadUsers();
          if (selectedUser?.user?.user_id === userId) {
            const res = await adminGetUserDetail(userId);
            setSelectedUser(res.data);
          }
          showToast('Trial terminato', 'success');
        } catch (err) {
          console.error('Error ending trial:', err);
          showToast('Errore', 'error');
        }
      },
      'warning'
    );
  };

  useEffect(() => {
    loadStats();
    // Light polling to keep sidebar badges (Bug, Assistenza) fresh.
    const id = setInterval(loadStats, 15000);
    return () => clearInterval(id);
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'posts') loadPosts();
    if (activeTab === 'reports') loadReports();
    if (activeTab === 'challenges') loadChallenges();
    if (activeTab === 'ads') loadAds();
    if (activeTab === 'settings') loadSettings();
    if (activeTab === 'chef') loadSettings();
    if (activeTab === 'legal') loadLegal();
    if (activeTab === 'credentials') loadCredentials();
  }, [activeTab, loadUsers, loadPosts, loadReports, loadAds, loadSettings, loadLegal, loadCredentials, loadChallenges]);

  // Auto-redirect when the active tab is no longer accessible to the
  // user's effective admin role (e.g. after a self-demote). Defined
  // here — before any early returns — to satisfy React Hooks rules.
  const _accessibleSections = user?.accessible_sections;
  useEffect(() => {
    if (!user?.is_admin) return;
    if (canAccessSection(_accessibleSections, activeTab)) return;
    // pick the first accessible tab
    const fallback = ['stats', 'analytics', 'bugs', 'support', 'moderation', 'users']
      .find((id) => canAccessSection(_accessibleSections, id));
    if (fallback) setActiveTab(fallback);
  }, [_accessibleSections, activeTab, user?.is_admin]);

  const handleBanUser = async (userId, banned) => {
    try {
      await adminBanUser(userId, banned);
      loadUsers();
      if (selectedUser?.user?.user_id === userId) {
        const res = await adminGetUserDetail(userId);
        setSelectedUser(res.data);
      }
    } catch (err) {
      console.error('Error banning user:', err);
    }
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      await adminToggleAdmin(userId, isAdmin);
      loadUsers();
    } catch (err) {
      console.error('Error toggling admin:', err);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    showConfirm(
      'Eliminare Utente',
      `Eliminare "${userName}"?\n\nQuesta azione è IRREVERSIBILE e cancellerà tutti i suoi dati.`,
      async () => {
        closeConfirm();
        try {
          await adminDeleteUser(userId);
          loadUsers();
          loadStats();
          if (selectedUser?.user?.user_id === userId) {
            setSelectedUser(null);
          }
          showToast('Utente eliminato', 'success');
        } catch (err) {
          showToast(getErrorMessage(err, 'Errore durante l\'eliminazione'), 'error');
        }
      },
      'danger'
    );
  };

  const handleWatchUser = async (userId, isWatched, userName) => {
    const action = isWatched ? 'attivare la sorveglianza per' : 'rimuovere la sorveglianza da';
    const explanation = isWatched 
      ? 'Se questo utente elimina il suo account, i suoi post verranno ANONIMIZZATI (non cancellati) e la sua email verrà BANNATA.'
      : 'L\'utente potrà eliminare il suo account normalmente.';
    
    showConfirm(
      isWatched ? 'Attiva Sorveglianza' : 'Rimuovi Sorveglianza',
      `${action} "${userName}"?\n\n${explanation}`,
      async () => {
        closeConfirm();
        try {
          await adminWatchUser(userId, isWatched, isWatched ? 'Segnalato da admin' : null);
          loadUsers();
          if (selectedUser?.user?.user_id === userId) {
            const res = await adminGetUserDetail(userId);
            setSelectedUser(res.data);
          }
          showToast(isWatched ? 'Utente sotto sorveglianza' : 'Sorveglianza rimossa', 'success');
        } catch (err) {
          showToast(getErrorMessage(err, 'Errore'), 'error');
        }
      },
      isWatched ? 'warning' : 'default'
    );
  };

  const handleDeletePost = async (postId) => {
    showConfirm(
      'Eliminare Post',
      'Sei sicuro di voler eliminare questo post?',
      async () => {
        closeConfirm();
        try {
          await adminDeletePost(postId);
          loadPosts();
          loadStats();
          showToast('Post eliminato', 'success');
        } catch (err) {
          console.error('Error deleting post:', err);
          showToast('Errore nell\'eliminazione', 'error');
        }
      },
      'danger'
    );
  };

  const handleReport = async (reportId, action) => {
    try {
      await adminHandleReport(reportId, action);
      loadReports();
      loadStats();
    } catch (err) {
      console.error('Error handling report:', err);
    }
  };

  const handleGenerateRecipe = async (autoPublish = false) => {
    setGenerating(true);
    setGeneratedRecipe(null);
    try {
      const res = await adminGenerateRecipe(selectedMealType, autoPublish);
      setGeneratedRecipe(res.data);
      if (autoPublish) {
        loadStats();
        loadPosts();
      }
    } catch (err) {
      console.error('Error generating recipe:', err);
    } finally {
      setGenerating(false);
    }
  };

  const viewUserDetail = async (userId) => {
    try {
      const res = await adminGetUserDetail(userId);
      setSelectedUser(res.data);
    } catch (err) {
      console.error('Error loading user detail:', err);
    }
  };

  const saveBanner = async (data) => {
    try {
      if (editingBanner) {
        await adminUpdateBanner(editingBanner.banner_id, data);
      } else {
        await adminCreateBanner(data);
      }
      setShowBannerModal(false);
      setEditingBanner(null);
      loadAds();
      showToast(editingBanner ? 'Banner aggiornato' : 'Banner creato', 'success');
    } catch (err) {
      console.error('Error saving banner:', err);
      showToast('Errore nel salvare il banner', 'error');
    }
  };

  const confirmDeleteBanner = async () => {
    if (!deletingBanner) return;
    try {
      await adminDeleteBanner(deletingBanner.banner_id);
      setDeletingBanner(null);
      loadAds();
    } catch (err) {
      console.error('Error deleting banner:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#E8334A]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-2xl p-6 text-center max-w-sm w-full">
          <AlertTriangle className="w-12 h-12 text-[#E8334A] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Accesso Negato</h2>
          <p className="text-gray-400 mb-4 text-sm">{error}</p>
          <button 
            onClick={() => navigate('/app/dashboard')} 
            className="w-full px-4 py-3 bg-[#E8334A] text-white rounded-xl font-medium"
          >
            Torna all'App
          </button>
        </div>
      </div>
    );
  }

  const menuItems = [
    { id: 'stats', label: 'Dashboard', icon: BarChart3 },
    { id: 'analytics', label: 'Statistiche', icon: BarChart3 },
    { id: 'transactions', label: 'Transazioni', icon: CreditCard },
    { id: 'queue', label: 'Task Queue', icon: Zap },
    { id: 'users', label: 'Utenti', icon: Users },
    { id: 'posts', label: 'Post', icon: FileText },
    { id: 'reports', label: 'Segnalazioni', icon: AlertTriangle, badge: stats?.reported_posts_pending },
    { id: 'reported_messages', label: 'Messaggi segnalati', icon: MessageSquare, badge: stats?.reported_messages_pending },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'bugs', label: 'Bug', icon: Bug, badge: stats?.bugs_open },
    { id: 'support', label: 'Assistenza', icon: Headphones, badge: stats?.support_unread },
    { id: 'challenges', label: 'Sfide', icon: Trophy },
    { id: 'move', label: 'Workout Move', icon: Dumbbell },
    { id: 'ads', label: 'Pubblicità', icon: Megaphone },
    { id: 'promo', label: 'Codici Promo', icon: Ticket },
    { id: 'lifetime_promo', label: 'Promo Lifetime', icon: Gift },
    { id: 'buttons', label: 'Bottoni Dashboard', icon: Palette },
    { id: 'communications', label: 'Comunicazioni', icon: MessageSquare },
    { id: 'reminders', label: 'Promemoria', icon: Bell },
    { id: 'chef', label: 'AI', icon: Sparkles },
    { id: 'legal', label: 'Legal', icon: ScrollText },
    { id: 'moderation', label: 'Moderazione', icon: ShieldAlert },
    { id: 'credentials', label: 'Credenziali API', icon: Key },
    { id: 'settings', label: 'Impostazioni', icon: Settings },
    { id: 'team', label: 'Team Admin', icon: ShieldCheck },
    { id: 'role_permissions', label: 'Permessi Ruoli', icon: Lock },
  ];

  // Filter sidebar by the backend-resolved accessible sections.
  const adminRole = user?.effective_admin_role || 'master';
  const visibleMenuItems = menuItems.filter((m) => canAccessSection(user?.accessible_sections, m.id));

  return (
    <div className="admin-page lg:flex lg:h-screen" data-testid="admin-panel">
      {/* Desktop Sidebar - Collapsible on lg+ */}
      <aside className={`hidden lg:flex lg:flex-col lg:flex-shrink-0 bg-gray-800 border-r border-gray-700 transition-all duration-200 relative ${
        sidebarCollapsed ? 'lg:w-16' : 'lg:w-72'
      }`}>
        {/* Toggle button — floating on the sidebar's right edge so it's
            always reachable and never gets confused with menu icons. */}
        <button
          onClick={toggleSidebar}
          className="absolute top-5 -right-3 w-6 h-6 rounded-full bg-[#E8334A] hover:bg-[#d12940] text-white flex items-center justify-center shadow-md z-10"
          data-testid="sidebar-toggle"
          title={sidebarCollapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
          aria-label={sidebarCollapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
        >
          {sidebarCollapsed
            ? <ChevronsRight className="w-3.5 h-3.5" />
            : <ChevronsLeft className="w-3.5 h-3.5" />}
        </button>

        {/* Sidebar Header */}
        <div className={`p-4 border-b border-gray-700 flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
          <div className="w-10 h-10 rounded-xl bg-[#E8334A] flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold">S</span>
          </div>
          {!sidebarCollapsed && (
            <span className="text-white font-bold text-lg truncate">Slimmy Admin</span>
          )}
        </div>

        {/* Menu Items */}
        <nav className={`flex-1 ${sidebarCollapsed ? 'p-2' : 'p-3'} space-y-1 overflow-y-auto`}>
          {visibleMenuItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                title={sidebarCollapsed ? item.label : undefined}
                className={`w-full flex items-center rounded-xl transition-all relative ${
                  sidebarCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3'
                } ${
                  isActive
                    ? 'bg-[#E8334A] text-white'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
                {/* When collapsed, show a tiny badge dot */}
                {sidebarCollapsed && item.badge > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 leading-[16px] text-[9px] font-bold rounded-full bg-red-500 text-white text-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Section */}
        <div className={`border-t border-gray-700 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 mb-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white">
                  {user?.name?.[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate text-sm">{user?.name}</p>
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
          )}
          <div className={`flex gap-2 ${sidebarCollapsed ? 'flex-col' : ''}`}>
            <button
              onClick={() => navigate('/app/dashboard')}
              title={sidebarCollapsed ? 'App' : undefined}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 text-sm ${
                sidebarCollapsed ? 'p-2.5' : 'px-3 py-2.5'
              }`}
            >
              <Home className="w-4 h-4" />
              {!sidebarCollapsed && 'App'}
            </button>
            <button
              onClick={logout}
              title={sidebarCollapsed ? 'Esci' : undefined}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 text-sm ${
                sidebarCollapsed ? 'p-2.5' : 'px-3 py-2.5'
              }`}
            >
              <LogOut className="w-4 h-4" />
              {!sidebarCollapsed && 'Esci'}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Wrapper for Desktop */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Mobile Header - Hidden on desktop */}
        <header className="lg:hidden flex-shrink-0 bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 rounded-lg hover:bg-gray-700 text-gray-400"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 rounded-lg bg-[#E8334A] flex items-center justify-center">
              <span className="text-white font-bold text-sm">S</span>
            </div>
            <h1 className="text-base font-bold text-white">
              {menuItems.find(m => m.id === activeTab)?.label}
            </h1>
          </div>
          <button 
            onClick={() => navigate('/app/dashboard')}
            className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">App</span>
          </button>
        </header>

        {/* Desktop Header */}
        <header className="hidden lg:flex flex-shrink-0 bg-gray-800/50 border-b border-gray-700 px-6 py-4 items-center justify-between">
          <h1 className="text-xl font-bold text-white">
            {menuItems.find(m => m.id === activeTab)?.label}
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">
              {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>
        </header>

      {/* Mobile Sidebar Overlay - Hidden on desktop */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 z-50"
          onClick={() => setSidebarOpen(false)}
        >
          <aside 
            className="absolute left-0 top-0 bottom-0 w-72 bg-gray-800 shadow-2xl flex flex-col"
            onClick={e => e.stopPropagation()}
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Sidebar Header — fixed at top */}
            <div className="flex-shrink-0 p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#E8334A] flex items-center justify-center">
                  <span className="text-white font-bold">S</span>
                </div>
                <span className="text-white font-bold">Slimmy Admin</span>
              </div>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-700 text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Items — scrollable middle area */}
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {visibleMenuItems.map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeTab === item.id 
                        ? 'bg-[#E8334A] text-white' 
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* User Section — fixed at bottom as flex sibling (not absolute) */}
            <div className="flex-shrink-0 p-4 border-t border-gray-700 bg-gray-800">
              <div className="flex items-center gap-3 mb-3">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white">
                    {user?.name?.[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate text-sm">{user?.name}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
              </div>
              <button 
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 text-sm"
              >
                <LogOut className="w-4 h-4" />
                Esci
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content - Scrollable */}
      <main className="admin-content p-4 pb-8 lg:p-6 lg:pb-8 lg:overflow-y-auto lg:flex-1">
        {/* Stats Tab — not lazy-loaded (shown by default as landing screen). */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            {/* Desktop: 3 columns, Mobile: 2 columns */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 lg:gap-4">
              <StatCard label="Utenti totali" value={stats.total_users} icon={Users} color="#3B82F6" />
              <StatCard label="Attivi (7g)" value={stats.active_users} icon={Users} color="#16A34A" />
              <StatCard label="Nuovi oggi" value={stats.new_users_today} icon={Users} color="#8B5CF6" />
              <StatCard label="Post totali" value={stats.total_posts} icon={FileText} color="#F59E0B" />
              <StatCard label="Challenge" value={stats.total_challenges} icon={Users} color="#EC4899" />
              <StatCard label="Pesate" value={stats.total_weight_logs} icon={Users} color="#14B8A6" />
            </div>

            {/* Live balances of every external paid service the platform
                depends on (Twilio, Stripe, fal.ai, Emergent LLM …).
                Master-only: contains sensitive financial info. */}
            {adminRole === 'master' && (
              <Suspense fallback={<div className="h-32 bg-gray-800/50 rounded-xl animate-pulse" />}>
                <ServiceBalances showToast={showToast} />
              </Suspense>
            )}

            {stats.reported_posts_pending > 0 && (
              <div 
                className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 cursor-pointer"
                onClick={() => setActiveTab('reports')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white">{stats.reported_posts_pending} post segnalati</p>
                    <p className="text-gray-400 text-sm">In attesa di revisione</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* All lazy-loaded tabs share a single Suspense boundary.
            A tab click triggers the chunk fetch; the skeleton is shown until
            the module resolves (typically <100ms on warm cache, 200-400ms on
            cold network). */}
        <Suspense fallback={<AdminTabSkeleton />}>
        {/* Users Tab (extracted) */}
        {activeTab === 'users' && (
          <UsersTab
            users={users}
            userSearch={userSearch}
            usersPage={usersPage}
            usersTotal={usersTotal}
            setUserSearch={setUserSearch}
            setUsersPage={setUsersPage}
            setGiftModal={setGiftModal}
            viewUserDetail={viewUserDetail}
            handleBanUser={handleBanUser}
            handleToggleAdmin={handleToggleAdmin}
            handleDeleteUser={handleDeleteUser}
            handleWatchUser={handleWatchUser}
            loadUsers={loadUsers}
            showToast={showToast}
          />
        )}

        {/* Posts Tab (extracted) */}
        {activeTab === 'posts' && (
          <PostsTab
            posts={posts}
            postsPage={postsPage}
            postsTotal={postsTotal}
            setPostsPage={setPostsPage}
            setPreviewPost={setPreviewPost}
            handleDeletePost={handleDeletePost}
          />
        )}

        {/* Reports Tab (extracted) */}
        {activeTab === 'reports' && (
          <ReportsTab reports={reports} handleReport={handleReport} />
        )}

        {/* Reported Messages — private 1:1 chat moderation queue */}
        {activeTab === 'reported_messages' && <ReportedMessagesTab />}
        {activeTab === 'payments' && <PaymentsTab />}

        {/* Challenges Tab (extracted) */}
        {activeTab === 'challenges' && (
          <ChallengesTab
            challenges={challenges}
            challengesTotal={challengesTotal}
            challengePage={challengePage}
            challengeStatus={challengeStatus}
            challengeSearch={challengeSearch}
            setChallengePage={setChallengePage}
            setChallengeStatus={setChallengeStatus}
            setChallengeSearch={setChallengeSearch}
            setDeleteChallengeModal={setDeleteChallengeModal}
            openChallengeDetail={openChallengeDetail}
          />
        )}

        {/* Ads Tab (extracted) */}
        {activeTab === 'ads' && (
          <AdsTab
            adStats={adStats}
            adConfig={adConfig}
            setAdConfig={setAdConfig}
            banners={banners}
            bannerFilter={bannerFilter}
            setBannerFilter={setBannerFilter}
            setEditingBanner={setEditingBanner}
            setShowBannerModal={setShowBannerModal}
            setDeletingBanner={setDeletingBanner}
            loadAds={loadAds}
          />
        )}

        {/* Chef AI Tab (extracted) */}
        {activeTab === 'chef' && (
          <ChefTab
            appSettings={appSettings}
            setAppSettings={setAppSettings}
            saveSettings={saveSettings}
            savingSettings={savingSettings}
            selectedMealType={selectedMealType}
            setSelectedMealType={setSelectedMealType}
            generating={generating}
            generatedRecipe={generatedRecipe}
            handleGenerateRecipe={handleGenerateRecipe}
          />
        )}

        {/* Communications Tab */}
        {activeTab === 'communications' && (
          <CommunicationsTab 
            appSettings={appSettings} 
            setAppSettings={setAppSettings}
            saveSettings={saveSettings}
            savingSettings={savingSettings}
          />
        )}

        {/* Reminders Tab */}
        {activeTab === 'reminders' && (
          <RemindersTab showToast={showToast} />
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promo' && (
          <PromoCodesTab showToast={showToast} />
        )}

        {/* Lifetime Promo Tab — first N users get free lifetime premium */}
        {activeTab === 'lifetime_promo' && (
          <LifetimePromoTab showToast={showToast} />
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && <TransactionsTab />}

        {/* Analytics / Statistiche Tab */}
        {activeTab === 'analytics' && <StatsTab />}

        {/* Task Queue Tab (ARQ worker observability) */}
        {activeTab === 'queue' && <QueueTab />}

        {/* Dashboard Buttons Tab */}
        {activeTab === 'buttons' && (
          <DashboardButtonsTab 
            dashboardButtons={dashboardButtons}
            setDashboardButtons={setDashboardButtons}
            savingButtons={savingButtons}
            setSavingButtons={setSavingButtons}
            editingButton={editingButton}
            setEditingButton={setEditingButton}
            showToast={showToast}
          />
        )}

        {/* Legal Tab (extracted) */}
        {activeTab === 'legal' && <LegalTab showToast={showToast} />}

        {/* Moderation Tab — custom banned words per language */}
        {activeTab === 'moderation' && <ModerationTab showToast={showToast} />}

        {/* Credentials Tab (extracted) */}
        {activeTab === 'credentials' && <CredentialsTab showToast={showToast} />}
        {activeTab === 'bugs' && <BugsTab showToast={showToast} />}
        {activeTab === 'support' && <SupportTab showToast={showToast} />}
        {activeTab === 'team' && <TeamTab showToast={showToast} currentUserId={user?.user_id} />}
        {activeTab === 'role_permissions' && <RolePermissionsTab showToast={showToast} onPermissionsChange={refreshUser} />}
        {activeTab === 'move' && <MoveTab showToast={showToast} />}

        {/* Settings Tab (extracted) */}
        {activeTab === 'settings' && (
          <SettingsTab
            appSettings={appSettings}
            setAppSettings={setAppSettings}
            saveSettings={saveSettings}
            savingSettings={savingSettings}
          />
        )}
        </Suspense>
      </main>
      </div> {/* End of Main Content Wrapper for Desktop */}

      {/* User Detail Modal */}
      {selectedUser && (
        <div 
          className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-0 sm:p-4"
          onClick={() => setSelectedUser(null)}
        >
          <div 
            className="w-full sm:max-w-lg bg-gray-800 rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">Dettagli Utente</h3>
              <button onClick={() => setSelectedUser(null)} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center mb-5">
              {selectedUser.user?.picture ? (
                <img src={selectedUser.user.picture} alt="" className="w-20 h-20 rounded-full mx-auto mb-3" />
              ) : (
                <div className="w-20 h-20 rounded-full mx-auto mb-3 bg-gray-600 flex items-center justify-center text-2xl text-white">
                  {selectedUser.user?.name?.[0]?.toUpperCase()}
                </div>
              )}
              <h4 className="text-lg font-bold text-white">{selectedUser.user?.name}</h4>
              {selectedUser.user?.email && (
                <p className="text-gray-400 text-sm">{selectedUser.user.email}</p>
              )}
              {selectedUser.user?.phone && (
                <p className="text-gray-400 text-sm">{selectedUser.user.phone}</p>
              )}
              {!selectedUser.user?.email && !selectedUser.user?.phone && (
                <p className="text-gray-500 text-sm italic">Nessun contatto</p>
              )}
            </div>

            {/* Contact & password management */}
            {!selectedUser.user?.is_admin && (
              <div className="pt-4 border-t border-gray-700 mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <span className="text-white font-medium">Contatti & Password</span>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="admin-edit-contact-btn"
                    onClick={() => setEditContactModal({
                      user_id: selectedUser.user?.user_id,
                      name: selectedUser.user?.name,
                      email: selectedUser.user?.email || '',
                      phone: selectedUser.user?.phone || ''
                    })}
                    className="flex-1 py-2 rounded-lg bg-blue-500/20 text-blue-400 text-sm font-medium flex items-center justify-center gap-1"
                  >
                    <Edit2 className="w-4 h-4" /> Modifica Email/Tel
                  </button>
                  <button
                    data-testid="admin-reset-password-btn"
                    onClick={() => setResetPasswordModal({
                      user_id: selectedUser.user?.user_id,
                      name: selectedUser.user?.name
                    })}
                    className="flex-1 py-2 rounded-lg bg-purple-500/20 text-purple-400 text-sm font-medium flex items-center justify-center gap-1"
                  >
                    <Key className="w-4 h-4" /> Reset Password
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="bg-gray-700 p-3 rounded-xl text-center">
                <p className="text-xl font-bold text-white">{selectedUser.posts_count}</p>
                <p className="text-xs text-gray-400">Post</p>
              </div>
              <div className="bg-gray-700 p-3 rounded-xl text-center">
                <p className="text-xl font-bold text-white">{selectedUser.badges?.length || 0}</p>
                <p className="text-xs text-gray-400">Badge</p>
              </div>
            </div>

            {selectedUser.plan && (
              <div className="bg-gray-700 p-3 rounded-xl mb-5">
                <p className="text-xs uppercase text-gray-400 mb-1">Piano attivo</p>
                <p className="text-white font-medium text-sm">
                  {selectedUser.plan.start_weight} kg → {selectedUser.plan.target_weight} kg
                </p>
              </div>
            )}

            {/* Premium management */}
            {!selectedUser.user?.is_admin && (
              <div className="pt-4 border-t border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-5 h-5 text-yellow-500" />
                  <span className="text-white font-medium">Stato Premium</span>
                </div>
                
                {selectedUser.user?.is_premium ? (
                  <div className="space-y-3">
                    <div className="bg-yellow-500/10 text-yellow-500 px-3 py-2 rounded-lg text-sm">
                      {selectedUser.user?.premium_lifetime 
                        ? '✓ Premium Lifetime' 
                        : `✓ Premium fino al ${new Date(selectedUser.user?.premium_expires_at).toLocaleDateString('it-IT')}`
                      }
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setGiftModal({ user_id: selectedUser.user?.user_id, name: selectedUser.user?.name })}
                        className="flex-1 py-2 rounded-lg bg-yellow-500/20 text-yellow-500 text-sm font-medium"
                      >
                        + Estendi
                      </button>
                      <button
                        onClick={() => revokePremium(selectedUser.user?.user_id)}
                        className="flex-1 py-2 rounded-lg bg-red-600/20 text-red-500 text-sm font-medium"
                      >
                        Revoca
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className={`px-3 py-2 rounded-lg text-sm ${
                      selectedUser.user?.trial_used 
                        ? 'bg-gray-700 text-gray-400' 
                        : 'bg-green-500/10 text-green-500'
                    }`}>
                      {selectedUser.user?.trial_used ? '✗ Trial già usato' : '○ Trial disponibile'}
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => setGiftModal({ user_id: selectedUser.user?.user_id, name: selectedUser.user?.name })}
                        className="flex-1 py-2.5 rounded-lg bg-yellow-500/20 text-yellow-500 font-medium flex items-center justify-center gap-1 text-sm"
                      >
                        <Gift className="w-4 h-4" /> Regala
                      </button>
                      {!selectedUser.user?.trial_used && (
                        <button
                          onClick={() => endTrial(selectedUser.user?.user_id)}
                          className="flex-1 py-2.5 rounded-lg bg-orange-500/20 text-orange-500 font-medium flex items-center justify-center gap-1 text-sm"
                        >
                          <Clock className="w-4 h-4" /> Termina Trial
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Delete user */}
            {!selectedUser.user?.is_admin && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <button
                  onClick={() => handleDeleteUser(selectedUser.user?.user_id, selectedUser.user?.name || selectedUser.user?.email)}
                  className="w-full py-2.5 rounded-lg bg-red-600/20 text-red-500 font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <UserX className="w-5 h-5" /> Elimina Utente
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Post Preview Modal */}
      {previewPost && (
        <div 
          className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-0 sm:p-4"
          onClick={() => setPreviewPost(null)}
        >
          <div 
            className="w-full sm:max-w-lg bg-gray-800 rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Anteprima Post</h3>
              <button onClick={() => setPreviewPost(null)} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-400 mb-1">{previewPost.user_name}</p>
              <p className="text-white">{previewPost.text}</p>
            </div>

            {previewPost.image_url && (
              <img 
                src={previewPost.image_url} 
                alt="" 
                className="w-full rounded-xl mb-4"
              />
            )}

            <div className="flex items-center gap-3 text-sm text-gray-400 mb-4">
              <span className="capitalize px-2 py-1 bg-gray-700 rounded">{previewPost.post_type}</span>
              <span>{new Date(previewPost.created_at).toLocaleString('it-IT')}</span>
            </div>

            <button
              onClick={() => {
                handleDeletePost(previewPost.post_id);
                setPreviewPost(null);
              }}
              className="w-full py-2.5 rounded-lg bg-red-500/20 text-red-400 font-medium text-sm"
            >
              Elimina Post
            </button>
          </div>
        </div>
      )}

      {/* Challenge Detail Modal */}
      {selectedChallenge && createPortal(
        <div 
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setSelectedChallenge(null)}
        >
          <div 
            className="w-full sm:max-w-2xl bg-gray-800 rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gray-800 p-5 border-b border-gray-700 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div 
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                  style={{ backgroundColor: (selectedChallenge.challenge?.color || '#E8334A') + '20' }}
                >
                  {selectedChallenge.challenge?.emoji || '🎯'}
                </div>
                <div className="min-w-0">
                  <h3 className="text-white font-bold truncate">{selectedChallenge.challenge?.name}</h3>
                  <p className="text-gray-400 text-xs">
                    {selectedChallenge.challenge?.start_date} → {selectedChallenge.challenge?.end_date}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelectedChallenge(null)} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {selectedChallenge.challenge?.description && (
                <p className="text-gray-300 text-sm">{selectedChallenge.challenge.description}</p>
              )}

              {/* Participants */}
              <div>
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Partecipanti ({selectedChallenge.challenge?.participants?.length || 0})
                </h4>
                <div className="space-y-2">
                  {(selectedChallenge.challenge?.participants || []).map(p => (
                    <div key={p.user_id} className="flex items-center gap-3 bg-gray-900 rounded-lg p-3">
                      {p.profile_photo ? (
                        <img src={p.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E8334A] to-[#C41E3A] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">{(p.user_name || '?')[0]?.toUpperCase()}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {p.user_name}
                          {p.user_id === selectedChallenge.challenge?.creator_id && (
                            <Crown className="w-3 h-3 inline ml-1 text-yellow-400" />
                          )}
                        </p>
                        <p className="text-gray-400 text-xs truncate">
                          {p.email || p.phone || '—'} · -{(p.weight_lost || 0).toFixed(1)} kg
                        </p>
                      </div>
                      {p.user_id !== selectedChallenge.challenge?.creator_id && (
                        <button
                          onClick={() => setRemoveParticipantModal({
                            challenge_id: selectedChallenge.challenge.challenge_id,
                            user_id: p.user_id,
                            user_name: p.user_name,
                          })}
                          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 flex-shrink-0"
                          title="Rimuovi"
                          data-testid={`admin-remove-participant-${p.user_id}`}
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Posts */}
              <div>
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Contenuti ({selectedChallenge.posts?.length || 0})
                </h4>
                {(selectedChallenge.posts || []).length === 0 ? (
                  <p className="text-gray-500 text-sm">Nessun post pubblicato</p>
                ) : (
                  <div className="space-y-2">
                    {(selectedChallenge.posts || []).map(post => (
                      <div key={post.post_id} className="bg-gray-900 rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-white text-sm font-medium truncate">{post.user_name}</span>
                          <span className="text-gray-500 text-xs flex-shrink-0">
                            {post.created_at ? new Date(post.created_at).toLocaleDateString('it-IT') : ''}
                          </span>
                        </div>
                        {post.text && <p className="text-gray-300 text-sm line-clamp-2">{post.text}</p>}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span>{post.post_type}</span>
                          {post.image_url && <span>🖼️</span>}
                          {post.video_url && <span>🎥</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Delete challenge button */}
              <div className="pt-2 border-t border-gray-700">
                <button
                  onClick={() => setDeleteChallengeModal(selectedChallenge.challenge)}
                  className="w-full py-3 rounded-lg bg-red-500/20 text-red-400 font-medium hover:bg-red-500/30 flex items-center justify-center gap-2"
                  data-testid="admin-challenge-detail-delete-btn"
                >
                  <Trash2 className="w-4 h-4" /> Elimina challenge
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Challenge Confirmation */}
      {deleteChallengeModal && createPortal(
        (() => {
          const participantCount = deleteChallengeModal.participants?.length || 0;
          const hasOthers = participantCount > 1;
          const KEYWORD = 'ELIMINA';
          const isMatch = deleteChallengeConfirmText.trim().toUpperCase() === KEYWORD;
          const canDelete = !hasOthers || isMatch;
          return (
        <div 
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => { setDeleteChallengeModal(null); setDeleteChallengeConfirmText(''); }}
        >
          <div 
            className="w-full max-w-sm bg-gray-800 rounded-2xl p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-center text-white mb-2">Eliminare la challenge?</h3>
            <p className="text-sm text-center text-gray-400 mb-4">
              <strong className="text-white">{deleteChallengeModal.name}</strong>
              {hasOthers
                ? ` ha ${participantCount} partecipanti. L'eliminazione è permanente e irreversibile. I post associati resteranno nel feed ma senza collegamento alla challenge.`
                : ' verrà eliminata in modo permanente. I post associati resteranno nel feed ma senza collegamento alla challenge.'
              }
            </p>
            {hasOthers && (
              <div className="mb-5">
                <p className="text-xs text-gray-300 mb-2">
                  Per confermare scrivi <span className="font-mono font-bold text-red-400">{KEYWORD}</span> qui sotto:
                </p>
                <input
                  type="text"
                  value={deleteChallengeConfirmText}
                  onChange={e => setDeleteChallengeConfirmText(e.target.value)}
                  placeholder={`Scrivi ${KEYWORD}`}
                  data-testid="admin-challenge-delete-confirm-input"
                  className={`w-full px-3 py-2.5 rounded-lg text-sm font-mono tracking-wider outline-none border-2 transition-colors bg-gray-900 text-white ${
                    isMatch ? 'border-red-500' : 'border-gray-700'
                  }`}
                  autoCapitalize="characters"
                  autoComplete="off"
                />
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setDeleteChallengeModal(null); setDeleteChallengeConfirmText(''); }} className="flex-1 py-2.5 rounded-lg bg-gray-700 text-gray-300 text-sm font-medium">Annulla</button>
              <button 
                onClick={confirmDeleteChallenge}
                disabled={!canDelete}
                data-testid="admin-delete-challenge-confirm"
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-1 transition-opacity ${
                  canDelete ? 'bg-red-500 text-white' : 'bg-red-500/40 text-white/70 cursor-not-allowed'
                }`}
              >
                <Trash2 className="w-4 h-4" /> Elimina
              </button>
            </div>
          </div>
        </div>
          );
        })(),
        document.body
      )}

      {/* Remove Participant Confirmation */}
      {removeParticipantModal && createPortal(
        <div 
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setRemoveParticipantModal(null)}
        >
          <div 
            className="w-full max-w-sm bg-gray-800 rounded-2xl p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <UserMinus className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-center text-white mb-2">Rimuovere il partecipante?</h3>
            <p className="text-sm text-center text-gray-400 mb-5">
              <strong className="text-white">{removeParticipantModal.user_name}</strong> verrà rimosso da questa challenge.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setRemoveParticipantModal(null)} className="flex-1 py-2.5 rounded-lg bg-gray-700 text-gray-300 text-sm font-medium">Annulla</button>
              <button 
                onClick={confirmRemoveParticipant}
                data-testid="admin-remove-participant-confirm"
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium"
              >
                Rimuovi
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Contact Modal */}
      {editContactModal && (
        <div 
          className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-0 sm:p-4"
          onClick={() => setEditContactModal(null)}
        >
          <div 
            className="w-full sm:max-w-md bg-gray-800 rounded-t-2xl sm:rounded-2xl p-5"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-bold text-white">Modifica Contatti</h3>
              </div>
              <button onClick={() => setEditContactModal(null)} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-400 mb-4 text-sm">
              Modifica email e/o telefono di <strong className="text-white">{editContactModal.name}</strong>
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email</label>
                <input
                  data-testid="admin-edit-contact-email-input"
                  type="email"
                  value={editContactModal.email}
                  onChange={(e) => setEditContactModal({ ...editContactModal, email: e.target.value })}
                  placeholder="utente@esempio.com"
                  className="w-full px-3 py-2.5 bg-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Telefono (formato E.164, es. +391234567890)</label>
                <input
                  data-testid="admin-edit-contact-phone-input"
                  type="tel"
                  value={editContactModal.phone}
                  onChange={(e) => setEditContactModal({ ...editContactModal, phone: e.target.value })}
                  placeholder="+391234567890"
                  className="w-full px-3 py-2.5 bg-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500">
                Lascia un campo vuoto per rimuoverlo. Almeno uno dei due deve essere valorizzato per permettere il login.
              </p>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setEditContactModal(null)}
                className="flex-1 py-2.5 rounded-lg bg-gray-700 text-gray-300 text-sm font-medium"
              >
                Annulla
              </button>
              <button
                data-testid="admin-edit-contact-save-btn"
                onClick={updateUserContact}
                className="flex-1 py-2.5 rounded-lg bg-blue-500 text-white text-sm font-medium"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <ResetPasswordDialog
          userName={resetPasswordModal.name}
          onCancel={() => setResetPasswordModal(null)}
          onConfirm={resetUserPassword}
        />
      )}

      {/* Gift Premium Modal */}
      {giftModal && (
        <div 
          className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-0 sm:p-4"
          onClick={() => setGiftModal(null)}
        >
          <div 
            className="w-full sm:max-w-md bg-gray-800 rounded-t-2xl sm:rounded-2xl p-5"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-yellow-500" />
                <h3 className="text-lg font-bold text-white">Regala Premium</h3>
              </div>
              <button onClick={() => setGiftModal(null)} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-400 mb-4 text-sm">
              Stai regalando Premium a <strong className="text-white">{giftModal.name}</strong>
            </p>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                <span className="text-white text-sm">Lifetime (illimitato)</span>
                <button
                  onClick={() => setGiftLifetime(!giftLifetime)}
                  className={`w-12 h-7 rounded-full transition-colors ${giftLifetime ? 'bg-yellow-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${giftLifetime ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {!giftLifetime && (
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Mesi</label>
                  <input
                    type="number"
                    min="1"
                    value={giftMonths}
                    onChange={(e) => setGiftMonths(parseInt(e.target.value) || 1)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                  />
                </div>
              )}

              <div>
                <label className="text-gray-400 text-xs block mb-1">Motivo (opzionale)</label>
                <input
                  type="text"
                  value={giftReason}
                  onChange={(e) => setGiftReason(e.target.value)}
                  placeholder="Es: Beta tester, Contest winner..."
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                />
              </div>

              <button
                onClick={giftPremium}
                className="w-full py-3 rounded-xl bg-yellow-500 text-black font-semibold text-sm"
              >
                Conferma Regalo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banner Edit Modal — lazy so the form code loads only when opened. */}
      {showBannerModal && (
        <Suspense fallback={null}>
          <BannerModal
            banner={editingBanner}
            onClose={() => { setShowBannerModal(false); setEditingBanner(null); }}
            onSave={saveBanner}
          />
        </Suspense>
      )}

      {/* Delete Banner Confirmation */}
      {deletingBanner && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4"
          onClick={() => setDeletingBanner(null)}
        >
          <div 
            className="w-full max-w-sm bg-gray-800 rounded-2xl p-5 text-center"
            onClick={e => e.stopPropagation()}
          >
            <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-white mb-2">Eliminare Banner?</h3>
            <p className="text-gray-400 text-sm mb-5">"{deletingBanner.title}" sarà eliminato permanentemente.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeletingBanner(null)}
                className="flex-1 py-2.5 rounded-xl bg-gray-700 text-gray-300 font-medium text-sm"
              >
                Annulla
              </button>
              <button
                onClick={confirmDeleteBanner}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium text-sm"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        type={confirmModal.type}
        confirmText="Conferma"
        cancelText="Annulla"
      />

      {/* Toast Notifications */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
}

// Banner Modal Component
